"use client";

import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { EngagementType, LikeState } from '@/lib/engagement';
import { toggleLike as toggleLikeApi } from '@/lib/engagement';

type Key = `${EngagementType}:${string}`;

type Store = Record<Key, LikeState>;

type EngagementCtx = {
  getState: (type: EngagementType, id: string | number) => LikeState | undefined;
  setState: (type: EngagementType, id: string | number, st: LikeState) => void;
  toggleLike: (type: EngagementType, id: string | number, session: any) => Promise<LikeState | undefined>;
};

const Ctx = createContext<EngagementCtx | null>(null);

export function EngagementProvider({ children }: { children: React.ReactNode }) {
  const [store, setStore] = useState<Store>({});
  const setState = useCallback((type: EngagementType, id: string | number, st: LikeState) => {
    const key: Key = `${type}:${String(id)}`;
    setStore((prev) => ({ ...prev, [key]: st }));
  }, []);
  const getState = useCallback((type: EngagementType, id: string | number) => {
    const key: Key = `${type}:${String(id)}`;
    return store[key];
  }, [store]);
  const toggleLike = useCallback(async (type: EngagementType, id: string | number, session: any) => {
    const current = getState(type, id);
    const next = await toggleLikeApi(type, id, session, current?.liked);
    if (next) setState(type, id, next);
    return next;
  }, [getState, setState]);

  const value = useMemo(() => ({ getState, setState, toggleLike }), [getState, setState, toggleLike]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useEngagement() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useEngagement must be used within EngagementProvider');
  return ctx;
}

